import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { convertEditorialHighlightToEditorialHighlightInput } from '../../utils/inputTransform';
import { addOffsetSeconds } from '../../utils/dateTransform';
import { createOrUpdateEditorialHighlight as upsertHighlightMutation } from './apiMutations';
import { getGameAirings } from '../games/apiQueries';
import { EditorialHighlight, Period, Airing } from '../../utils/types';

const injectPlayIds = (periods: Period[]): Period[] => {
	const withPlayIds = cloneDeep(periods).map((period: Period) => {
		if (!period.playId) period.playId = uuidv4();
		return period;
	});

	return withPlayIds;
};

const createNewEditorialHighlight = (channelId: string | null, offset: number | null, periods: Period[]): EditorialHighlight => {
	const newPeriods = cloneDeep(periods).map((period: Period) => {
		if (channelId) period.source.channelId = channelId;

		if (offset) {
			period.source.startTime = addOffsetSeconds(period.source.startTime, offset);
			period.source.endTime = addOffsetSeconds(period.source.endTime, offset);
		}

		return period;
	});

	return {
		periods: {
			items: newPeriods
		}
	};
};

const getEditorialHighlightIdData = (sport: string | null, gameId: string, airingId: string): { [key: string]: string } => {
	if ((sport && sport.toLowerCase() === 'golf') || !airingId) {
		return { gameId };
	} else {
		return {
			gameId,
			airingId
		};
	}
};

const getOffset = (airing: Airing): number | null => {
	if (airing.offsets?.items?.length) {
		const offsets = airing.offsets?.items.sort((a, b): number => (a.segment > b.segment ? 1 : b.segment > a.segment ? -1 : 0));
		return offsets[0].offset;
	}

	return null;
};

const getSportDraftStatus = (sport: string | null): boolean => {
	if (!sport) return true;

	switch (sport.toLowerCase()) {
		case 'football':
			return false;
		case 'cricket':
			return true;
		case 'f1':
			return true;
		case 'golf':
			return true;
		case 'americanfootball':
			return false;
		default:
			return true;
	}
};

const getIsMultiSave = (sport: string | null): boolean => {
	if (!sport) return false;

	switch (sport.toLowerCase()) {
		case 'f1':
			return true;

		default:
			return false;
	}
};

export default function useEditorialHighlightSave(sport: string | null, gameId: string, airing: Airing): SaveFnType {
	const [upsertHighlight] = useMutation(upsertHighlightMutation);
	const apolloClient = useApolloClient();
	const draft = getSportDraftStatus(sport);

	const saveEditorialHighlightForAiring = async (
		draft: boolean,
		gameId: string,
		airingId: string,
		channelId: string | null,
		offset: number | null,
		editorialPeriods: Period[]
	): Promise<boolean> => {
		await upsertHighlight({
			variables: {
				editorialHighlight: {
					draft,
					...convertEditorialHighlightToEditorialHighlightInput(createNewEditorialHighlight(channelId, offset, editorialPeriods!)),
					...getEditorialHighlightIdData(sport, gameId, airingId)
				}
			}
		});

		return Promise.resolve(true);
	};

	const getAiringsForGame = async (gameId: string): Promise<Airing[] | []> => {
		try {
			const { data } = await apolloClient.query({
				query: getGameAirings,
				variables: {
					id: decodeURIComponent(gameId)
				},
				fetchPolicy: 'no-cache'
			});

			return data?.getGame?.airings?.items || [];
		} catch (e) {
			throw new Error('Could not find Airings');
		}
	};

	const save: SaveFnType = async (editorialPeriods: Period[]): Promise<boolean> => {
		const editorialPeriodsWithPlayIds = injectPlayIds(editorialPeriods);

		return saveEditorialHighlightForAiring(draft, gameId, airing.id, null, null, editorialPeriodsWithPlayIds);
	};

	const multiSave: SaveFnType = async (editorialPeriods: Period[]): Promise<boolean> => {
		const editorialPeriodsWithPlayIds = injectPlayIds(editorialPeriods);
		const isFirstEditorialHighlightSaved = await save(editorialPeriodsWithPlayIds);

		if (isFirstEditorialHighlightSaved) {
			const allAirings = await getAiringsForGame(gameId);
			const siblingAirings = allAirings.filter((siblingAiring: Airing) => siblingAiring.id !== airing.id);

			const promises: Promise<boolean>[] = [];
			siblingAirings.forEach((airing: Airing) => {
				promises.push(saveEditorialHighlightForAiring(draft, gameId, airing.id, airing.channelId, getOffset(airing), editorialPeriodsWithPlayIds));
			});

			const result = await Promise.all(promises);
			const allSuccessful = !result.includes(false);
			return Promise.resolve(allSuccessful);
		}

		return Promise.resolve(false);
	};

	return getIsMultiSave(sport) ? multiSave : save;
}

type SaveFnType = (editorialPeriods: Period[]) => Promise<boolean>;
